import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCloseCoachingActionPlanMutation, useCalculateNextBestActionAssociateMutation, useInsertCoachingSessionMutation } from '../coachingActionPlanSlice';
import correctiveActionVector from '../../../assets/correctiveActionVector.png';
import finalizeVector from '../../../assets/finalizeVector.png';
import closeActionPlanVector from '../../../assets/closeActionPlanVector.png';
import recognizeVector from '../../../assets/recognizeVector.png';
import followUpVector from '../../../assets/followUpVector.png';
import CoachingShoutOut from '../../../components/coachingShoutOut/coachingShoutOUt';
import { CoachingEntityTypes, CoachingTabs, CaMenuTabindex } from '../../../utils/typeConstants';
import { CAMENU } from '../../../constants';
import { OverridePerformancePopup } from '../../../components/coachingActionPlan/overridePerformancePopup/OverridePerformancePopup';

interface OtherActionsSectionProps {
    CAPSession: any;
    latestSession?: any;
    onActionPlanClosed?: () => void;
}

export const OtherActionsSection: React.FC<OtherActionsSectionProps> = ({ CAPSession, latestSession, onActionPlanClosed }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [showCorrectiveDialog, setShowCorrectiveDialog] = useState(false);
    const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [shoutoutVisible, setShoutoutVisible] = useState<boolean>(false);
    const [shoutoutResult, setShoutoutResult] = useState<any>(null);
    const [closeActionPlan, { isLoading: isClosing }] = useCloseCoachingActionPlanMutation();
    const [calculateNextBestAction] = useCalculateNextBestActionAssociateMutation();
    const [insertCoachingSession] = useInsertCoachingSessionMutation();
    const [showOverride, setShowOverride] = useState<boolean>(false);

    const handleFinalizeActionPlan = async () => {
        if (!CAPSession?.coachingActionPlanId) {
            toast.error('No action plan ID available');
            return;
        }

        try {
            await closeActionPlan(CAPSession.coachingActionPlanId).unwrap();
            await calculateNextBestAction({
                scorecardVersionId: CAPSession.scorecardVersionId,
                employeeNumber: CAPSession.employeeNumber,
            }).unwrap();
            setShowFinalizeDialog(false);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Close action plan failed:', error);
            toast.error('Failed to close action plan. Please try again.');
        }
    };

    useEffect(() => {
        if (shoutoutResult === 'Shoutout saved successfully') {
            handleFinalizeActionPlan();
            setShoutoutResult(null);
        }
    }, [shoutoutResult]);

    const handleCreateNewFollowUp = () => {
        if (!CAPSession?.coachingActionPlanId || !latestSession) {
            toast.error('No action plan data available');
            return;
        }

        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilMonday = (8 - dayOfWeek) % 7 || 7;
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + daysUntilMonday);

        const newSession = {
            coachingActionPlanId: CAPSession.coachingActionPlanId,
            sessionNumber: latestSession?.sessionNumber === null || latestSession?.sessionNumber === undefined ? 0 : latestSession.sessionNumber + 1,
            metricCurrentValue: latestSession?.metricResultValue,
            metricTargetValue: latestSession?.metricTargetValue,
            coachingActionPlanSessionId: latestSession?.coachingActionPlanSessionId,
            followUpDate: nextMonday.toISOString(),
            performanceCommitment: latestSession?.performanceCommitment,
            coachingSkillId: latestSession?.coachingSkillId,
            coachingBehaviorId: latestSession?.coachingBehaviorId,
            coachingActionId: latestSession?.coachingActionId,
            actionDescription: latestSession?.actionDescription,
            successCriteria: latestSession?.successCriteria,
            notes: latestSession?.notes,
            analysisNotes: latestSession?.analysisNotes,
            frameNotes: latestSession?.frameNotes,
            understandNotes: latestSession?.understandNotes,
            exploreNotes: latestSession?.exploreNotes,
            sessionCommitmentAttained: latestSession?.sessionCommitmentAttained,
            scorecardMetricId: CAPSession?.scorecardMetricId,
            coachingNextBestActionId: CAPSession?.coachingNextBestActionId,
            analysisTypeIds: latestSession?.analysisTypes ?? [],
        };

        insertCoachingSession(newSession)
            .unwrap()
            .then((res: any) => {
                if (res?.data) {
                    toast.success('New follow up session created successfully');
                } else {
                    toast.error('Error creating new follow up session');
                }
            })
            .catch(() => {
                toast.error('Error creating new follow up session');
            });
    };

    const isRecognizeStatus = CAPSession?.coachingNextBestActionTypeId === 2;
    const isFollowUpStatus = CAPSession?.coachingNextBestActionTypeId === 3;

    return (
        <>
            <div className="review-adjust__other">
                <div
                    className={`review-adjust__other-header ${expanded ? 'expanded' : ''}`}
                    onClick={() => setExpanded(!expanded)}
                    onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && setExpanded(!expanded)}
                    role="button" tabIndex={0}
                >
                    <h4>Other Actions</h4>
                    <i className="pi pi-chevron-down" />
                </div>
                {expanded && (
                    <div className="review-adjust__other-content">
                        {isRecognizeStatus && (
                            <>
                                <div className="review-adjust__pill-group">
                                    <div
                                        className="review-adjust__pill review-adjust__pill--follow-up"
                                        onClick={handleCreateNewFollowUp}
                                        onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleCreateNewFollowUp()}
                                        role="button" tabIndex={0}
                                    >
                                        <span>Start New Follow Up</span>
                                        <span className="review-adjust__pill-divider" />
                                        <img src={followUpVector} alt="" className="review-adjust__pill-icon" />
                                    </div>
                                </div>

                                <span className="review-adjust__or">or</span>
                            </>
                        )}

                        <div className="review-adjust__pill-group">
                            <div
                                className="review-adjust__pill review-adjust__pill--corrective"
                                onClick={() => setShowCorrectiveDialog(true)}
                                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && setShowCorrectiveDialog(true)}
                                role="button" tabIndex={0}
                            >
                                <span>Issue a Corrective Action</span>
                                <span className="review-adjust__pill-divider" />
                                <img src={correctiveActionVector} alt="" className="review-adjust__pill-icon" />
                            </div>
                            <p className="review-adjust__pill-desc">*This will close the current Action Plan Module</p>
                        </div>

                        <span className="review-adjust__or">or</span>

                        <div className="review-adjust__pill-group">
                            <div
                                className="review-adjust__pill review-adjust__pill--finalize"
                                onClick={() => setShowFinalizeDialog(true)}
                                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && setShowFinalizeDialog(true)}
                                role="button" tabIndex={0}
                            >
                                <span>Finalize Action Plan</span>
                                <span className="review-adjust__pill-divider" />
                                <img src={finalizeVector} alt="" className="review-adjust__pill-icon" />
                            </div>
                            <p className="review-adjust__pill-desc">*Close this action plan regardless of the goal</p>
                        </div>

                        {isFollowUpStatus && (
                            <>
                                <span className="review-adjust__or">or</span>

                                <div className="review-adjust__pill-group">
                                    <div
                                        className="review-adjust__pill review-adjust__pill--recognize"
                                        onClick={() => setShowOverride(true)}
                                        onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && setShowOverride(true)}
                                        role="button" tabIndex={0}
                                    >
                                        <span>Recognize and Close</span>
                                        <span className="review-adjust__pill-divider" />
                                        <img src={recognizeVector} alt="" className="review-adjust__pill-icon" />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <Dialog
                visible={showCorrectiveDialog}
                onHide={() => setShowCorrectiveDialog(false)}
                className="ra-dialog"
                contentClassName="ra-dialog__content"
                style={{ borderRadius: '24px', overflow: 'hidden' }}
                modal draggable={false} resizable={false} closable={false} showHeader={false}
            >
                <div className="ra-dialog__icon"><i className="pi pi-exclamation-triangle" /></div>
                <h2 className="ra-dialog__title">Warning</h2>
                <p className="ra-dialog__message">This will send you to the Corrective Actions Menu.<br />Are you sure you want to continue?</p>
                <div className="ra-dialog__actions">
                    <Button label="Cancel" className="ra-dialog__btn-secondary" onClick={() => setShowCorrectiveDialog(false)} />
                    <Button
                        label="Continue"
                        className="ra-dialog__btn-primary"
                        onClick={() => {
                            navigate(
                                `${CAMENU}/${CaMenuTabindex.AssignmentUpdates}/cap/${CAPSession?.employeeNumber}/${CAPSession?.coachingActionPlanId}/${CAPSession?.scorecardVersionId}`,
                                { replace: true }
                            );
                        }}
                    />
                </div>
            </Dialog>

            <Dialog
                visible={showFinalizeDialog}
                onHide={() => setShowFinalizeDialog(false)}
                className="ra-dialog"
                contentClassName="ra-dialog__content"
                style={{ borderRadius: '24px', overflow: 'hidden' }}
                modal draggable={false} resizable={false} closable={false} showHeader={false}
            >
                <div className="ra-dialog__icon"><i className="pi pi-exclamation-triangle" /></div>
                <h2 className="ra-dialog__title">Warning</h2>
                <p className="ra-dialog__message">
                    This will close all entries for this Action Plan.<br />
                    Are you sure you want to continue?
                </p>
                <div className="ra-dialog__actions">
                    <Button
                        label="Return"
                        className="ra-dialog__btn-secondary"
                        onClick={() => setShowFinalizeDialog(false)}
                        disabled={isClosing}
                    />
                    <Button
                        label="Close Action Plan"
                        className="ra-dialog__btn-primary"
                        onClick={handleFinalizeActionPlan}
                        loading={isClosing}
                    />
                </div>
            </Dialog>

            <Dialog
                visible={showSuccessDialog}
                onHide={() => {
                    setShowSuccessDialog(false);
                    onActionPlanClosed?.();
                }}
                className="ra-dialog ra-dialog--success"
                contentClassName="ra-dialog__content"
                style={{ borderRadius: '24px', overflow: 'hidden', width: '600px', maxWidth: '95vw' }}
                modal draggable={false} resizable={false} closable={false} showHeader={false}
            >
                <div className="ra-dialog__icon--success">
                    <img src={closeActionPlanVector} alt="Success" className="ra-dialog__success-img" />
                </div>
                <h2 className="ra-dialog__title--success">Action Plan Closed</h2>
                <p className="ra-dialog__message">This Action Plan has been closed.</p>
                <div className="ra-dialog__actions">
                    <Button
                        label="Back to Coaching"
                        className="ra-dialog__btn-gradient"
                        onClick={() => {
                            setShowSuccessDialog(false);
                            onActionPlanClosed?.();
                        }}
                    />
                </div>
            </Dialog>

            {shoutoutVisible && (
                <CoachingShoutOut
                    receiverId={CAPSession?.employeeNumber}
                    receiverName={CAPSession?.employeeName}
                    visible={shoutoutVisible}
                    setVisible={setShoutoutVisible}
                    coachingEntityTypeId={CoachingEntityTypes.CoachingActionPlan}
                    coachingEntityId={CAPSession?.coachingActionPlanId}
                    scorecardMetricId={CAPSession?.scorecardMetricId}
                    coreValueId={0}
                    via={CoachingTabs.CoachingActionPlan}
                    result={setShoutoutResult}
                />
            )}
            <OverridePerformancePopup
                visible={showOverride}
                metricName={CAPSession?.label}
                currentPerformance={latestSession?.metricCurrentValue ?? undefined}
                metricTypeId={CAPSession?.metricTypeId}
                metricTypeName={CAPSession?.metricTypeName}
                recognize
                CAPCurrentSession={latestSession}
                onCancel={() => setShowOverride(false)}
                onContinueToRecognition={() => {
                    setShowOverride(false);
                    setShoutoutVisible(true);
                }}
                coachingActionPlanId={CAPSession?.coachingActionPlanId}
            />
        </>
    );
};
